import React, { useState, useEffect } from 'react';
import DataEntryModal from '@grnhse/seedling/lib/birch/components/modals/data_entry_modal';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid_v2';
import {
  PrimaryButton,
  SecondaryButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import { Body } from '@grnhse/seedling/lib/birch/typography';
import Checkbox from '@grnhse/seedling/lib/birch/components/checkbox';
import RadioGroup from '@grnhse/seedling/lib/birch/components/radio_group';
import TextArea from '@grnhse/seedling/lib/birch/components/forms/text_area';
import Divider from '@grnhse/seedling/lib/birch/components/divider';
import { withLocalePrefix } from 'shared/utils/translation';
import { flashSuccess, flashError } from 'flash/flash_manager';
import { trackEvent } from '@grnhse/datacoral_tracker';
import { MatchStrength } from 'talent_matching/types';
import StrengthIndicator from 'talent_matching/components/shared/strength_indicator';

export type RatingValue =
  | 'definitely_not'
  | 'no'
  | 'yes'
  | 'strong_yes'
  | 'undecided';

const t = withLocalePrefix('talent_matching.internal_feedback');
const tRoot = withLocalePrefix('talent_matching');

const RATING_LABEL_KEYS: Record<RatingValue, string> = {
  definitely_not: 'ratings.definitely_not',
  no: 'ratings.no',
  yes: 'ratings.yes',
  strong_yes: 'ratings.strong_yes',
  undecided: 'ratings.undecided',
};

const reasonOptionKeys = [
  'skills_experience',
  'work_preferences',
  'eligibility',
  'not_enough_info',
  'other',
];

const undecidedReasonOptionKeys = [
  'not_enough_info',
  'not_fully_reviewed',
  'comparing',
  'requirements_changing',
  'error',
  'other',
];

const matchScoreOptionKeys = ['mostly_matched', 'somewhat_off', 'didnt_match'];

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  candidateName: string;
  candidateId: number;
  hiringPlanId: number;
  selectedRating: RatingValue;
  matchStrength: MatchStrength | null;
};

const InternalFeedbackModal = ({
  isOpen,
  onClose,
  onSuccess,
  candidateName,
  candidateId,
  hiringPlanId,
  selectedRating,
  matchStrength,
}: Props) => {
  const [checkedReasons, setCheckedReasons] = useState<Set<string>>(new Set());
  const [matchScoreReflection, setMatchScoreReflection] = useState<string>('');
  const [additionalNotes, setAdditionalNotes] = useState('');

  useEffect(() => {
    if (isOpen) {
      setCheckedReasons(new Set());
      setMatchScoreReflection('');
      setAdditionalNotes('');
    }
  }, [isOpen]);

  const handleReasonToggle = (value: string) => {
    setCheckedReasons((prev) => {
      const next = new Set(prev);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  };

  const handleSave = () => {
    try {
      trackEvent('mygh_candidate_feedback', {
        job_seeker_id: candidateId,
        hiring_plan_id: hiringPlanId,
        rating: selectedRating,
        reasons: Array.from(checkedReasons),
        match_score_reflection: matchScoreReflection,
        additional_notes: additionalNotes,
      });
      flashSuccess(t('modal.actions.save_success'));
      onSuccess();
      onClose();
    } catch {
      flashError(t('modal.actions.save_error'));
    }
  };

  const ratingLabel = t(RATING_LABEL_KEYS[selectedRating]);
  const reasonKeys =
    selectedRating === 'undecided'
      ? undecidedReasonOptionKeys
      : reasonOptionKeys;
  const reasonPrefix =
    selectedRating === 'undecided'
      ? 'modal.undecided_reasons'
      : 'modal.reasons';
  const reasons = reasonKeys.map((key) => ({
    value: key,
    label: t(`${reasonPrefix}.${key}`),
  }));
  const matchScoreOptions = matchScoreOptionKeys.map((key) => ({
    value: key,
    label: t(`modal.match_score.${key}`),
  }));

  return (
    <DataEntryModal
      isOpen={isOpen}
      size="medium"
      onDismiss={onClose}
      ariaLabel="internal-feedback-modal"
    >
      <DataEntryModal.Header onClick={onClose}>
        <span className="pii-blur">
          {t('modal.title', { name: candidateName, rating: ratingLabel })}
        </span>
      </DataEntryModal.Header>
      <DataEntryModal.Body>
        <Stack spacing={3}>
          <Body>{t('modal.select_all')}</Body>

          <Stack spacing={1.5}>
            <Body medium>{t('modal.reasons_heading')}</Body>
            <Stack spacing={1}>
              {reasons.map((option) => (
                <Checkbox
                  key={option.value}
                  label={option.label}
                  value={option.value}
                  checked={checkedReasons.has(option.value)}
                  onChange={() => handleReasonToggle(option.value)}
                />
              ))}
            </Stack>
          </Stack>

          <Stack spacing={1.5}>
            <Body medium>{t('modal.match_score_heading')}</Body>
            {matchStrength && matchStrength !== 'unknown' && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Body>
                  {t('modal.match_score_label', {
                    name: candidateName.split(' ')[0],
                    score: tRoot(`match_strengths.${matchStrength}`),
                  })}
                </Body>
                <StrengthIndicator matchStrength={matchStrength} />
              </Stack>
            )}
            <RadioGroup
              legend=""
              value={matchScoreReflection}
              onChange={(selected) =>
                setMatchScoreReflection(selected as string)
              }
              options={matchScoreOptions}
            />
          </Stack>

          <TextArea
            label=""
            ariaLabel="Additional explanation"
            placeholder={t('modal.notes_placeholder')}
            value={additionalNotes}
            onChange={(e) => setAdditionalNotes(e.target.value)}
          />
        </Stack>
      </DataEntryModal.Body>
      <DataEntryModal.Footer>
        <Divider />
        <Grid container spacing={1} sx={{ paddingTop: 2 }}>
          <Grid>
            <SecondaryButton onClick={onClose}>
              {t('modal.actions.cancel')}
            </SecondaryButton>
          </Grid>
          <Grid>
            <PrimaryButton onClick={handleSave}>
              {t('modal.actions.save')}
            </PrimaryButton>
          </Grid>
        </Grid>
      </DataEntryModal.Footer>
    </DataEntryModal>
  );
};

export default InternalFeedbackModal;
